import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ChartCard = ({ stock, currentFilters, currentPage, stockIndex }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Get trend category
  const getTrendCategory = (stock) => {
    const trendState = parseInt(stock.trend_state);
    const signalScore = parseInt(stock.signal_score);
    const predictedScore = parseInt(stock.predicted_signal_score);

    if (trendState === 1) {
      // UP Trend
      if (signalScore >= 3 && predictedScore >= 3) {
        return { label: 'UpTrend-Strong', color: 'signal-bullish' };
      } else if (signalScore < 3 && predictedScore < 3) {
        return { label: 'UpTrend-Weakening', color: 'signal-neutral' };
      } else if (signalScore >= 3 && predictedScore === 0) {
        return { label: 'UpTrend-NearReversal', color: 'signal-bearish' };
      } else {
        return { label: 'UpTrend', color: 'signal-bullish' };
      }
    } else {
      // DOWN Trend
      if (signalScore < 3 && predictedScore < 3) {
        return { label: 'DownTrend-Declining', color: 'signal-bearish' };
      } else if (signalScore > 3 && predictedScore > 3) {
        return { label: 'DownTrend-Improving', color: 'signal-neutral' };
      } else if (signalScore > 3 && predictedScore === 5) {
        return { label: 'DownTrend-NearReversal', color: 'signal-bullish' };
      } else {
        return { label: 'DownTrend', color: 'signal-bearish' };
      }
    }
  };

  const trendCategory = getTrendCategory(stock);

  // Handle image loading error
  const handleImageError = () => {
    setImageError(true);
  };

  // Format metrics (avoid treating ratios like P/E as percentages)
  const PERCENT_KEYS = new Set([
    'returnOnEquityTTM',
    'grossProfitMarginTTM',
    'netProfitMarginTTM',
    'debtRatioTTM',
    'revenue_cagr',
    'revenue_1yr_growth',
    'revenue_3yr_growth',
    'grossProfit_1yr_growth',
    'grossProfit_3yr_growth',
    'forecast_next_year_revenue_growth_rate',
    'forecast_next_year_profit_growth_rate',
    'eps_cagr',
    'rule_of_40'
  ]);

  const CURRENCY_KEYS = new Set(['dcf']);

  const formatValue = (key, val) => {
    if (val === undefined || val === null || val === '') return 'N/A';

    const num = typeof val === 'string' ? Number(val) : val;
    const isNum = typeof num === 'number' && !Number.isNaN(num);

    if (CURRENCY_KEYS.has(key) && isNum) return `$${num.toFixed(2)}`;
    if (PERCENT_KEYS.has(key) && isNum) return `${Math.round(num * 100)}%`;

    if (isNum) return num.toFixed(2);
    return String(val);
  };

  // Get image URL
  const getImageUrl = () => {
    const BASE_IMG_URL = process.env.REACT_APP_CHART_IMAGE_BASE_URL || 'https://www.swingtradertrends.com/rj/img/';

    if (stock.short_fname) {
      if (stock.short_fname.startsWith('http') || stock.short_fname.startsWith('data:')) {
        return stock.short_fname;
      }
      return BASE_IMG_URL + stock.short_fname;
    }

    return null;
  };

  // Keep a simple percent formatter for a couple of tech fields
  const formatNumber = (num) => {
    if (num === undefined || num === null) return 'N/A';
    const n = typeof num === 'string' ? Number(num) : num;
    if (typeof n === 'number' && !Number.isNaN(n)) {
      if (Math.abs(n) < 10) return Math.round(n * 100) + '%';
      return n.toFixed(2);
    }
    return String(num);
  };

  // Handle chart image click
  const handleChartClick = () => {
    navigate(`/chart/${stock.id}`, {
      state: {
        filters: currentFilters || {},
        page: currentPage || 1,
        stockIndex: stockIndex || 0
      }
    });
  };

  return (
    <div className="ste-card group">
      {/* Card Header with Signal Badge */}
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-navy-900 mb-1">{stock.symbol}</h3>
            <p className="text-xs text-gray-600 truncate">{stock.companyName}</p>
          </div>
          
          {/* Trend Category Badge */}
          <div className="flex flex-col items-end ml-3">
            <div className={`px-3 py-1.5 rounded-lg font-bold text-xs ${trendCategory.color}`}>
              {trendCategory.label}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              SS: {stock.signal_score}/5
            </div>
          </div>
        </div>

        {/* Predicted Direction */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center space-x-2">
            <span className="text-gray-500">Predicted:</span>
            <div className={`flex items-center font-semibold ${
              stock.predicted_direction === '↑' || stock.predicted_direction === 'up' ? 'text-signal-bullish' : 'text-signal-bearish'
            }`}>
              {stock.predicted_direction === '↑' || stock.predicted_direction === 'up' ? '↑' : '↓'}
              <span className="ml-1">{stock.predicted_signal_score}/5</span>
            </div>
          </div>
          <div className="text-gray-400">
            {stock.n_days_since_cross_over}d trend
          </div>
        </div>
      </div>

      {/* Chart Image */}
      <div 
        className="relative aspect-video bg-gray-50 cursor-pointer overflow-hidden group-hover:bg-gray-100 transition-colors"
        onClick={handleChartClick}
        title="Click to view larger chart"
      >
        {imageError || !getImageUrl() ? (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="mt-2 text-sm">Chart not available</p>
              <p className="text-xs mt-1">Click for details</p>
            </div>
          </div>
        ) : (
          <>
            <img
              src={getImageUrl()}
              alt={`${stock.symbol} chart`}
              className="w-full h-full object-contain"
              onError={handleImageError}
              loading="lazy"
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white rounded-lg px-4 py-2 shadow-lg">
                <span className="text-sm font-medium text-gray-700">View Details →</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Card Content */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-3 text-sm mb-3">
          <div>
            <span className="text-gray-500 text-xs">Trend:</span>
            <p className="font-medium text-gray-900">{stock.trend_comment || 'N/A'}</p>
          </div>
          <div>
            <span className="text-gray-500 text-xs">RSI:</span>
            <p className="font-medium text-gray-900">{stock.rsi ? stock.rsi.toFixed(1) : 'N/A'}</p>
          </div>
        </div>

        {/* Sector / Industry */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
          <span className="px-2 py-0.5 bg-gray-100 rounded">{stock.sector}</span>
          <span className="px-2 py-0.5 bg-gray-100 rounded">{stock.industry}</span>
        </div>

        {/* Expandable Details */}
        <div className={`overflow-hidden transition-all duration-300 ${expanded ? 'max-h-96' : 'max-h-0'}`}>
          <div className="pt-3 border-t border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-2 text-sm">Financial Metrics</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {/* Phase 3.1 metrics list (card expanded view) */}
              {stock.returnOnEquityTTM !== undefined && (
                <div><span className="text-gray-500">ROE:</span>{' '}<span className="font-medium">{formatValue('returnOnEquityTTM', stock.returnOnEquityTTM)}</span></div>
              )}
              {stock.priceSalesRatioTTM !== undefined && (
                <div><span className="text-gray-500">P/S:</span>{' '}<span className="font-medium">{formatValue('priceSalesRatioTTM', stock.priceSalesRatioTTM)}</span></div>
              )}
              {stock.priceEarningsRatioTTM !== undefined && (
                <div><span className="text-gray-500">P/E:</span>{' '}<span className="font-medium">{formatValue('priceEarningsRatioTTM', stock.priceEarningsRatioTTM)}</span></div>
              )}
              {stock.priceEarningsToGrowthRatioTTM !== undefined && (
                <div><span className="text-gray-500">PEG:</span>{' '}<span className="font-medium">{formatValue('priceEarningsToGrowthRatioTTM', stock.priceEarningsToGrowthRatioTTM)}</span></div>
              )}
              {stock.grossProfitMarginTTM !== undefined && (
                <div><span className="text-gray-500">Gross Margin:</span>{' '}<span className="font-medium">{formatValue('grossProfitMarginTTM', stock.grossProfitMarginTTM)}</span></div>
              )}
              {stock.netProfitMarginTTM !== undefined && (
                <div><span className="text-gray-500">Net Margin:</span>{' '}<span className="font-medium">{formatValue('netProfitMarginTTM', stock.netProfitMarginTTM)}</span></div>
              )}
              {stock.debtRatioTTM !== undefined && (
                <div><span className="text-gray-500">Debt Ratio:</span>{' '}<span className="font-medium">{formatValue('debtRatioTTM', stock.debtRatioTTM)}</span></div>
              )}
              {stock.piotroskiScore !== undefined && (
                <div><span className="text-gray-500">Piotroski:</span>{' '}<span className="font-medium">{formatValue('piotroskiScore', stock.piotroskiScore)}</span></div>
              )}
              {stock.eps_pos_qtrs !== undefined && (
                <div><span className="text-gray-500">EPS + qtrs:</span>{' '}<span className="font-medium">{formatValue('eps_pos_qtrs', stock.eps_pos_qtrs)}</span></div>
              )}
              {stock.revenue_cagr !== undefined && (
                <div><span className="text-gray-500">Rev CAGR:</span>{' '}<span className="font-medium">{formatValue('revenue_cagr', stock.revenue_cagr)}</span></div>
              )}
              {stock.revenue_pos_trend_yrs !== undefined && (
                <div><span className="text-gray-500">Rev + yrs:</span>{' '}<span className="font-medium">{formatValue('revenue_pos_trend_yrs', stock.revenue_pos_trend_yrs)}</span></div>
              )}
              {/* Removed SMA 10/200, SMA 30/200, SMA 50/200 per UI cleanup */}
              {stock.revenue_1yr_growth !== undefined && (
                <div><span className="text-gray-500">Rev 1Y:</span>{' '}<span className="font-medium">{formatValue('revenue_1yr_growth', stock.revenue_1yr_growth)}</span></div>
              )}
              {stock.revenue_3yr_growth !== undefined && (
                <div><span className="text-gray-500">Rev 3Y:</span>{' '}<span className="font-medium">{formatValue('revenue_3yr_growth', stock.revenue_3yr_growth)}</span></div>
              )}
              {stock.grossProfit_1yr_growth !== undefined && (
                <div><span className="text-gray-500">GP 1Y:</span>{' '}<span className="font-medium">{formatValue('grossProfit_1yr_growth', stock.grossProfit_1yr_growth)}</span></div>
              )}
              {stock.grossProfit_3yr_growth !== undefined && (
                <div><span className="text-gray-500">GP 3Y:</span>{' '}<span className="font-medium">{formatValue('grossProfit_3yr_growth', stock.grossProfit_3yr_growth)}</span></div>
              )}
              {stock.forecast_next_year_revenue_growth_rate !== undefined && (
                <div><span className="text-gray-500">Fcst Rev:</span>{' '}<span className="font-medium">{formatValue('forecast_next_year_revenue_growth_rate', stock.forecast_next_year_revenue_growth_rate)}</span></div>
              )}
              {stock.forecast_next_year_profit_growth_rate !== undefined && (
                <div><span className="text-gray-500">Fcst Profit:</span>{' '}<span className="font-medium">{formatValue('forecast_next_year_profit_growth_rate', stock.forecast_next_year_profit_growth_rate)}</span></div>
              )}
              {stock.eps_cagr !== undefined && (
                <div><span className="text-gray-500">EPS CAGR:</span>{' '}<span className="font-medium">{formatValue('eps_cagr', stock.eps_cagr)}</span></div>
              )}
              {stock.z_score_200d !== undefined && (
                <div><span className="text-gray-500">Z 200d:</span>{' '}<span className="font-medium">{formatValue('z_score_200d', stock.z_score_200d)}</span></div>
              )}
              {stock.z_score_60d !== undefined && (
                <div><span className="text-gray-500">Z 60d:</span>{' '}<span className="font-medium">{formatValue('z_score_60d', stock.z_score_60d)}</span></div>
              )}
              {stock.z_score_21d !== undefined && (
                <div><span className="text-gray-500">Z 21d:</span>{' '}<span className="font-medium">{formatValue('z_score_21d', stock.z_score_21d)}</span></div>
              )}
              {stock.dcf !== undefined && (
                <div><span className="text-gray-500">DCF:</span>{' '}<span className="font-medium">{formatValue('dcf', stock.dcf)}</span></div>
              )}
              {stock.price_to_dcf_ratio !== undefined && (
                <div><span className="text-gray-500">P/DCF:</span>{' '}<span className="font-medium">{formatValue('price_to_dcf_ratio', stock.price_to_dcf_ratio)}</span></div>
              )}
              {stock.rule_of_40 !== undefined && (
                <div><span className="text-gray-500">Rule of 40:</span>{' '}<span className="font-medium">{formatValue('rule_of_40', stock.rule_of_40)}</span></div>
              )}
              {stock.ratingRecommendation !== undefined && (
                <div><span className="text-gray-500">Rating:</span>{' '}<span className="font-medium">{formatValue('ratingRecommendation', stock.ratingRecommendation)}</span></div>
              )}
              {stock.holdings !== undefined && (
                <div className="col-span-2"><span className="text-gray-500">Holdings:</span>{' '}<span className="font-medium">{formatValue('holdings', stock.holdings)}</span></div>
              )}
            </div>

            <h4 className="font-semibold text-gray-900 mt-4 mb-2 text-sm">Technical Indicators</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {stock.sma50_slope !== undefined && (
                <div>
                  <span className="text-gray-500">SMA50:</span>{' '}
                  <span className={`font-medium ${stock.sma50_slope > 0 ? 'text-signal-bullish' : 'text-signal-bearish'}`}>
                    {stock.sma50_slope > 0 ? '↑ Positive' : '↓ Negative'}
                  </span>
                </div>
              )}
              {stock.sma200_slope !== undefined && (
                <div>
                  <span className="text-gray-500">SMA200:</span>{' '}
                  <span className={`font-medium ${stock.sma200_slope > 0 ? 'text-signal-bullish' : 'text-signal-bearish'}`}>
                    {stock.sma200_slope > 0 ? '↑ Positive' : '↓ Negative'}
                  </span>
                </div>
              )}
              {stock.pcnt_from_sma200 !== undefined && (
                <div>
                  <span className="text-gray-500">% from SMA200:</span>{' '}
                  <span className="font-medium">{formatNumber(stock.pcnt_from_sma200)}</span>
                </div>
              )}
              {stock.pcnt_from_sma100 !== undefined && (
                <div>
                  <span className="text-gray-500">% from SMA100:</span>{' '}
                  <span className="font-medium">{formatNumber(stock.pcnt_from_sma100)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Expand/Collapse Button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-3 text-sm text-brand-500 hover:text-brand-600 focus:outline-none font-medium"
        >
          {expanded ? '↑ Show Less' : '↓ Show More'}
        </button>
      </div>
    </div>
  );
};

export default ChartCard;
